import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AddonData, LogicStateType } from 'types'
import { rehydrateFromJSON } from '../../globalActions/globalActions'

export const initialState: LogicStateType = {
  basket: [],
  itemOrder: [],
  basketAddons: [],
  sortedByKey: undefined,
  sortedByHistory: ['default'],
  pcwPickedProduct: [],
  pcwMoreInfoClicked: {},
}

const logicStoreSlice = createSlice({
  name: 'logicStore',
  initialState,
  reducers: {
    addItemToBasket(state, action: PayloadAction<string>): void {
      state.basket.push(action.payload)
    },
    addPCWItemsToBasket(state, action: PayloadAction<string>): void {
      state.pcwPickedProduct.push(action.payload)
    },

    emptyBasket(state): void {
      state.basket = []
      state.basketAddons = []
      state.pcwPickedProduct = []
    },

    setItemOrder(state, action: PayloadAction<number[]>): void {
      state.itemOrder = action.payload
    },
    addAddonToBasket(state, action: PayloadAction<AddonData>): void {
      const addonToAdd = action.payload

      // Add if either this isn't a mutex addon or we don't have a mutex addon in the store already
      if (
        !addonToAdd.mutex ||
        state.basketAddons.every((addon) => !addon.mutex)
      ) {
        state.basketAddons.push(addonToAdd)
      }
    },
    removeAddon(state, action: PayloadAction<AddonData>): void {
      state.basketAddons = state.basketAddons.filter(
        (basketAddon) => basketAddon.id !== action.payload.id
      )
    },

    /**
     * Register that the more info button has been clicked for a particular plan
     *
     * This will increment the number of clicks for that plan by one if it already exists.
     * If not, then it will add the plan to the dictionary and set the number to one.
     */
    addPCWMoreInfoClick(state, action: PayloadAction<string>): void {
      const pageKey = action.payload
      if (state.pcwMoreInfoClicked[pageKey]) {
        state.pcwMoreInfoClicked[pageKey] += 1
      } else {
        state.pcwMoreInfoClicked[pageKey] = 1
      }
    },

    setSortedByKey(state, action: PayloadAction<string>): void {
      state.sortedByKey = action.payload
      state.sortedByHistory?.push(action.payload)
    },

    clearSortedByKey(state): void {
      state.sortedByKey = undefined
      state.sortedByHistory?.push('default')
    },
  },
  extraReducers: (builder) => {
    builder.addCase(rehydrateFromJSON, (state, action) => {
      const newState: { logic?: LogicStateType } = JSON.parse(action.payload)
      return newState.logic || state
    })
  },
})

export const {
  addItemToBasket,
  emptyBasket,
  setItemOrder,
  addAddonToBasket,
  removeAddon,
  addPCWItemsToBasket,
  addPCWMoreInfoClick,
  setSortedByKey,
  clearSortedByKey,
} = logicStoreSlice.actions

export const { reducer: logicStoreReducer } = logicStoreSlice
